import * as http from 'http';
import { useMemo } from 'react';
import { ApolloClient, ApolloLink, HttpLink, NormalizedCacheObject } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import merge from 'deepmerge';
import isEqual from 'lodash/isEqual';
import Sentry from 'utils/shared/sentry';
import { createApolloCache } from 'utils/client/apolloCache';
import { getViewerToken } from 'services/client/token';

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

export interface InitApolloOptions {
  clientState?: object;
  headers?: http.IncomingHttpHeaders;
  authToken?: string;
}

type Client = ApolloClient<NormalizedCacheObject>;
let apolloClient: Client;
const hasSyntheticUser =
  process.env.NODE_ENV !== 'production' &&
  (process.env.NEXT_PUBLIC_DEV_VIEWER_TIER === 'free' ||
    process.env.NEXT_PUBLIC_DEV_VIEWER_TIER === 'pro');
const URI =
  typeof window !== 'undefined' && hasSyntheticUser
    ? '/api/_/dev-graphql'
    : process.env.GRAPHQL_URL;

const withToken = setContext(async () => {
  const token = await getViewerToken();

  if (!token) {
    return {};
  }

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
});

function createApolloClient(options: InitApolloOptions = { headers: {} }) {
  const httpLink = new HttpLink({
    uri: URI,
    fetchOptions: {
      mode: 'cors',
    },
    headers: {
      ...(options.headers || {}),
    },
    credentials: 'same-origin', // Additional fetch() options like `credentials` or `headers`
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message }) => {
        Sentry.withScope((_scope) => {
          Sentry.captureMessage(message);
        });
      });
    }
    if (networkError) {
      Sentry.withScope((_scope) => {
        Sentry.captureException(networkError);
      });
    }
  });

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([withToken, errorLink, httpLink]),
    cache: createApolloCache(),
  });
}

export function initializeApollo(initialState: any = null) {
  const _apolloClient = apolloClient ?? createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
      ],
    });

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function addApolloState(client: Client, pageProps: any) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function useApollo(pageProps: any) {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const store = useMemo(() => initializeApollo(state), [state]);
  return store;
}
